<template>
  <div>
    <v-skeleton-loader v-if="loading && !registration" type="list-item-three-line, list-item-three-line" />
    <v-alert v-else-if="error" type="error" dense outlined>{{ error }}</v-alert>

    <template v-else-if="registration">
      <!-- Qui : de quoi décider sans ouvrir le dossier -->
      <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
        <v-icon>mdi-clipboard-account-outline</v-icon>
        <span class="text-h6 font-weight-regular">{{ registrationName(registration) }}</span>
        <v-chip small :color="registrationStatusColor(registration.status)" dark>{{ $t(`onboarding.registration.status.${registration.status}`) }}</v-chip>
      </div>
      <v-simple-table dense class="registration-panel__facts mb-3">
        <tbody>
          <tr>
            <th>{{ $t('organization.organization') }}</th>
            <td><router-link target="_blank" :to="{ name: 'organizations.detail', params: { id: registration.organizationId } }">{{ registration.organizationName || '—' }}</router-link></td>
          </tr>
          <tr><th>{{ $t('email') }}</th><td>{{ registration.email }}</td></tr>
          <tr v-if="registration.applicant && registration.applicant.phone"><th>{{ $t('onboarding.order-panel.phone') }}</th><td>{{ registration.applicant.phone }}</td></tr>
          <tr><th>{{ $t('onboarding.registration.postal-address') }}</th><td>{{ registrationAddress(registration) }}</td></tr>
          <tr v-for="field in registration.customFields || []" :key="field.id">
            <th>{{ field.label }}</th><td>{{ field.type === 'bool' ? $t(field.value ? 'yes' : 'no') : field.value }}</td>
          </tr>
          <tr>
            <th>{{ $t('onboarding.registration.payment-card') }}</th>
            <td>{{ paymentMethodLabel(registration.paymentMethod) || $t(registration.requiredGates.includes('payment-method') ? 'onboarding.registration.payment-awaited' : 'onboarding.registration.payment-not-required') }}</td>
          </tr>
          <tr>
            <th>{{ $t('onboarding.configuration.setting.membership.equipment') }}</th>
            <td>
              <template v-if="(registration.equipment || []).length">{{ registration.equipment.map(kind => $t(`onboarding.commissioning.kind.${kind}`)).join(', ') }}</template>
              <span v-else>{{ $t('onboarding.registration.no-equipment') }}</span>
            </td>
          </tr>
          <tr><th>{{ $t('onboarding.registration.source-label') }}</th><td>{{ $t(`onboarding.registration.source.${registration.source}`) }} · {{ registration.createdAt | formatDate }}</td></tr>
        </tbody>
      </v-simple-table>

      <!-- Le geste : valider ou refuser, une fois que la personne a fini sa part -->
      <v-alert v-if="registration.status !== 'awaiting-approval'" type="info" dense text class="mb-0">
        {{ $t('onboarding.order-panel.registration.already-decided', { status: $t(`onboarding.registration.status.${registration.status}`) }) }}
      </v-alert>
      <v-alert v-else-if="!registration.completeForApproval" type="info" dense text class="mb-0">{{ $t('onboarding.registration.expected.applicant-first') }}</v-alert>
      <v-alert v-else-if="!mayApproveMembers" type="warning" dense text class="mb-0">{{ $t('onboarding.registration.expected.approver-needed') }}</v-alert>
      <div v-else class="d-flex align-center">
        <span class="text-body-2">{{ $t('onboarding.registration.expected.approve') }}</span>
        <v-spacer />
        <registration-approval-actions :registration-id="registration.id" @decided="onDecided" />
      </div>
    </template>
  </div>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import RegistrationMixin from '../RegistrationMixin'
import RegistrationApprovalActions from './RegistrationApprovalActions.vue'

/**
 * Une inscription à valider, dans le tiroir de la file : qui, pour quelle organisation, ce qu'elle a renseigné et
 * donné, puis valider ou refuser. Le dossier complet reste à un clic.
 */
export default {
  name: 'RegistrationApprovalPanel',

  components: { RegistrationApprovalActions },

  mixins: [RegistrationMixin],

  props: {
    registrationId: { type: String, required: true },
  },

  data() {
    return { registration: null, loading: false, error: null }
  },

  watch: {
    registrationId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.registration = await RegistrationRepository.get(this.registrationId)
      } catch (e) {
        this.registration = null
        this.error = e?.response?.data?.message || this.$t('error-occurred')
      } finally {
        this.loading = false
      }
    },

    async onDecided() {
      await this.load()
      this.$emit('changed')
    },
  },
}
</script>

<style scoped>
.registration-panel__facts th {
  width: 180px;
  color: #616161;
  font-weight: 400;
  text-align: left;
}
</style>
